import { useDispatch, useSelector } from "react-redux";
import React, { useMemo, useState } from "react";

import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  MenuList,
  Popover,
  Typography,
  ListItemIcon,
} from "@mui/material";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import PersonIcon from "@mui/icons-material/Person";

import { logout } from "../../actions/auth";
import LogoSrc from "../../assets/images/logo.svg";
import { Link, useNavigate } from "react-router-dom";
import { saveCarePlan } from "../../actions/saveCarePlan";
import useClientId from "../../hooks/useClientId";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Loading } from "./Loading";
import Box from "@mui/material/Box";
import { alpha } from "@mui/material/styles";
import moment from "moment";
import { useMsal } from "@azure/msal-react";

// ----------------------------------------------------------------------
const SnowFlakes = () => {
  let shouldSnowFlaks = false;
  const currentMonth = moment().month() + 1;
  const currentDate = moment().date();

  if (
    (currentMonth === 11 && currentDate >= 24) ||
    (currentMonth === 1 && currentDate <= 24)
  ) {
    shouldSnowFlaks = true;
  }
  //
  if (!shouldSnowFlaks) {
    return null;
  }

  return (
    <div className="snowflakes" aria-hidden="true">
      <div className="snowflake">
        <div className="inner">❅</div>
      </div>
      <div className="snowflake">
        <div className="inner">❆</div>
      </div>
      <div className="snowflake">
        <div className="inner">❅</div>
      </div>
      <div className="snowflake">
        <div className="inner">❆</div>
      </div>
      <div className="snowflake">
        <div className="inner">❅</div>
      </div>
      <div className="snowflake">
        <div className="inner">❆</div>
      </div>
      <div className="snowflake">
        <div className="inner">❅</div>
      </div>
      <div className="snowflake">
        <div className="inner">❆</div>
      </div>
      <div className="snowflake">
        <div className="inner">❅</div>
      </div>
      <div className="snowflake">
        <div className="inner">❆</div>
      </div>
      <div className="snowflake">
        <div className="inner">❅</div>
      </div>
      <div className="snowflake">
        <div className="inner">❆</div>
      </div>
    </div>
  );
};

const AccountPopover = () => {
  const [open, setOpen] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const auth = useSelector((state) => state.auth);
  const { instance, accounts } = useMsal();

  // handlers
  const handleLogout = () => {
    setOpenConfirmation(true);
    setOpen(null);

  };

  const handleCloseConfirmation = () => setOpenConfirmation(false);

  const handleConfirmLogOut = () => {
    if (accounts.length > 0) {
      instance.logoutPopup();
    }
    dispatch(logout());
    navigate(0);

  };

  const handleOpen = (event) => setOpen(event.currentTarget);

  const handleClose = () => setOpen(null);

  const handleProfile = () => {
    navigate("/profile");
  };

  const account = useMemo(() => {
    if (localStorage.getItem("user")) {
      const userData = JSON.parse(localStorage.getItem("user"));

      return {
        photoURL: "photoURL",
        displayName: userData.user?.loginName,
        email: userData.user?.nameFull,
      };
    }
  }, [auth]);

  return (
    <>
      <IconButton
        size={"small"}
        onClick={handleOpen}
        sx={{
          // width: 20,
          // height: 20,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          ...(open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        <Avatar
          src={account?.photoURL ?? ""}
          alt={account?.displayName ?? ""}
          sx={{
            // width: 16,
            // height: 16,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
          }}
        >
          {account?.displayName.substring(0, 2).toUpperCase()}
        </Avatar>
      </IconButton>

      <Popover
        open={!!open}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          sx: {
            p: 2,
            mt: 0,
            ml: 0.75,
            width: 300,
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2 }}>
          <Typography variant="subtitle2" noWrap>
            {account?.displayName}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
            {account?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: "dashed" }} />
        <MenuList>
          <MenuItem
            disableRipple
            disableTouchRipple
            onClick={handleProfile}
            sx={{ typography: "body2" }}
          >
            <Grid container direction={"row"} spacing={2}>
              <Grid item xs={3}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
              </Grid>
              <Grid item>
                <Typography component={"h4"} variant="body2">
                  Profile
                </Typography>
              </Grid>
            </Grid>
          </MenuItem>
          <Divider sx={{ borderStyle: "dashed", m: 0 }} />
          <MenuItem
            disableRipple
            disableTouchRipple
            onClick={handleLogout}
            sx={{ typography: "body2" }}
          >
            <Grid container direction={"row"} spacing={2}>
              <Grid item xs={3}>
                <ListItemIcon>
                  <ExitToAppIcon fontSize="small" />
                </ListItemIcon>
              </Grid>
              <Grid item>
                <Typography component={"h4"} variant="body2">
                  Logout
                </Typography>
              </Grid>
            </Grid>
          </MenuItem>
        </MenuList>
      </Popover>

      {openConfirmation && (
        <Dialog
          open={openConfirmation}
          onClose={handleCloseConfirmation}
          aria-labelledby="alert-logout"
          aria-describedby="alert-logut"
        >
          <DialogTitle align={"center"} id="alert-logout-title">
            Logout?
          </DialogTitle>
          <DialogContent dividers={true}>
            Changes you have made may not be saved.
          </DialogContent>
          <DialogActions>
            <Button
              color={"default"}
              variant="outlined"
              onClick={handleCloseConfirmation}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmLogOut}
            >
              Log out
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

const Navbar = () => {
  // hooks
  const loader = useSelector((state) => state.appLoader);
  const dispatch = useDispatch();

  // variables
  const { clientId } = useClientId();

  const handleSaveCarePlanData = () => dispatch(saveCarePlan(clientId, true));

  return (
    <Grid>
      <Grid className="app-header">
        <Grid
          container
          direction={"row"}
          justifyContent={"space-between"}
          sx={{
            padding: "4px 16px",
            backgroundColor: "#253342",
            boxShadow: "0px 1px 12px 1px #111315",
            marginBottom: "1px",
          }}
        >
          <Grid item>
            <Link to={"/"} onClick={handleSaveCarePlanData}>
              <img alt="brand-logo" src={LogoSrc} width="50" height="40" />
            </Link>
          </Grid>
          <Grid item>
            <AccountPopover />
          </Grid>
        </Grid>
      </Grid>

      <ToastContainer position={"top-right"} autoClose={3000} />
      <Loading fullScreen open={loader.isOpen} message={loader.message} />
      {/*<SnowFlakes />*/}
    </Grid>
  );
};
export default Navbar;
